import buttonHandler from './helpers/buttonHandler';
import changeCheckboxesToBoolean from './helpers/changeCheckboxesToBoolean';

export enum RecaptchaStates {
    // eslint-disable-next-line no-unused-vars
    TRUE = 'true',
    // eslint-disable-next-line no-unused-vars
    FALSE = 'false',
}

interface ContactFormInitData {
    opportunityModule: any;
    grecaptchaModule: any;
    recapchaKey: string;
    sessionId: string;
    visitData: any;
    sentryLogUrl: string;
    referrer: string;
    languages: string;
    formName: string;
    urlParams: string;
    oppoApiKey?: string;
    optWidgetIds?: Record<string, HTMLElement>;
}

class ContactForm implements ContactFormInitData {
    opportunityModule: any;
    grecaptchaModule: any;

    isOpportunityLoaded: boolean;
    recapchaKey: string;

    sessionId: string;
    visitData: any;
    sentryLogUrl: string;
    referrer: string;
    languages: string;
    formName: string;
    urlParams: string;
    oppoApiKey: string;

    optWidgetIds?: Record<string, HTMLElement>;

    constructor({ oppoApiKey, ...data }: ContactFormInitData) {
        this.renderRecaptcha = this.renderRecaptcha.bind(this);

        Object.assign(this, data);
        this.oppoApiKey = oppoApiKey ?? '';
    }

    onGenerateEmail(
        event: any,
        data: any,
        predefinedTopic: { value: string; text: string }
    ): void {
        const correlationId = data?.correlationId || '';
        const formElement = event.target;
        const mailtoLink = document.createElement('a');

        formElement.style.display = 'none';

        mailtoLink.href = `mailto:${predefinedTopic.value}+${correlationId}@sagiton.pl?subject=${predefinedTopic.text}`;
        mailtoLink.textContent = `${predefinedTopic.value}+${correlationId}@sagiton.pl`;
        mailtoLink.style.overflowWrap = 'break-word';
        mailtoLink.style.wordBreak = 'break-all';
        mailtoLink.style.color = '#fff';
        mailtoLink.style.fontSize = '18px';
        mailtoLink.classList.add('form-generated-link');
        mailtoLink.setAttribute(
            'data-cy',
            'generate-contact-form-success-email'
        );

        formElement.parentNode.insertBefore(
            mailtoLink,
            formElement.nextSibling
        );
    }

    async onSubmit(
        event: any,
        additionalFormParams: any,
        // eslint-disable-next-line no-unused-vars
        cb?: (data: any) => void
    ) {
        const formElement = event.target;
        const successElement = formElement.nextSibling;
        const errorElement = successElement.nextSibling;

        const [loading, loaded] = buttonHandler(formElement);

        try {
            const opportunity = this.opportunityModule;

            const AdditionalParams: Record<string, string> = {
                environment: process.env.OPPORTUNITY_URL,
                cookieDomain: 'sagiton.pl',
            };

            const formData = new FormData(formElement);

            formData.append('url_params', this.urlParams);
            formData.append('form_name', this.formName);
            formData.append('referrer', this.referrer);
            formData.append('language', this.languages);
            formData.append('session_id', this.sessionId);
            formData.append('sentry_log_url', this.sentryLogUrl);
            formData.append('visit_data', this.visitData);

            Object.entries(additionalFormParams).forEach(([key, value]) => {
                if (!formData.has(key.toString())) {
                    formData.append(key, value as Blob);
                }
            });

            for (const [key, value] of Object(formData).entries()) {
                if (value === '') {
                    formData.delete(key);
                }
            }

            loading();

            if (this.oppoApiKey) {
                AdditionalParams.apiKey = this.oppoApiKey;
                AdditionalParams.XDisableRecaptcha = RecaptchaStates.TRUE;
            } else {
                const token = await this.renderRecaptcha(formElement);
                if (token === '') return;
                formData.append('recaptcha', token);
            }

            const data = await opportunity
                .sendOpportunity(
                    changeCheckboxesToBoolean(formData),
                    AdditionalParams
                )
                .catch((err: Error) => {
                    console.log('err', err);
                    throw err;
                });

            if (data.error) throw new Error(data.error);

            if (formElement && formElement.style) {
                formElement.style.display = 'none';
            }

            formElement.reset();

            cb && cb(data);

            if (successElement && successElement.style) {
                successElement.style.display = 'block';
            }
            if (errorElement && errorElement.style) {
                errorElement.style.display = 'none';
            }
        } catch (err) {
            if (errorElement && errorElement.style) {
                errorElement.style.display = 'block';
                errorElement.style.backgroundColor = 'transparent';
            }

            throw new Error(err);
        } finally {
            loaded();
        }
    }
    async wait(time = 100) {
        return new Promise((resolve) => {
            setTimeout(() => {
                resolve('');
            }, time);
        });
    }
    async renderRecaptcha(el: HTMLElement) {
        let ready = true;
        if (!this.grecaptchaModule.render) {
            ready = false;
            this.grecaptchaModule.ready(() => {
                ready = true;
            });
        }
        while (!ready) {
            await this.wait();
        }
        let recaptchToken = '';
        let isClodsedByUser = false;
        const div = document.createElement('div');
        el.appendChild(div);

        const recaptchWidgetId = this.grecaptchaModule.render(div, {
            sitekey: this.recapchaKey,
            size: 'invisible',
            hl: this.languages,
            callback: (token: string) => {
                recaptchToken = token;
            },
        }) as string;
        await this.grecaptchaModule.execute();

        this.waitForCloseRecaptcha().then(async () => {
            isClodsedByUser = true;
        });

        while (!recaptchToken) {
            if (isClodsedByUser) {
                break;
            } else {
                await this.wait();
            }
        }

        this.removeRecaptcha(recaptchWidgetId, div);

        return recaptchToken;
    }

    async waitForCloseRecaptcha() {
        await this.wait(1000);

        return new Promise(function (resolve) {
            const recaptchaWindow = document.querySelectorAll(
                "iframe[src*='google.com/recaptcha/api2/bframe'"
            )[0]?.parentNode?.parentNode as HTMLDivElement;

            if (recaptchaWindow) {
                new MutationObserver(() => {
                    if (recaptchaWindow && recaptchaWindow.style) {
                        recaptchaWindow.style.opacity == '0';
                        recaptchaWindow.style.zIndex = '-1000';
                    }

                    resolve('');
                }).observe(recaptchaWindow, {
                    attributeFilter: ['style'],
                });
            } else {
                return resolve('');
            }
        });
    }

    private removeRecaptcha(
        recaptchWidgetId: number | string,
        el: HTMLElement
    ) {
        this.grecaptchaModule.reset(recaptchWidgetId);
        el.remove();
    }
}

export default ContactForm;
